import { getAttributionPayload, getTestModeFlags } from "@/lib/utm";

declare global {
  interface Window {
    gtag?: (
      command: "event" | "config" | "js" | "set",
      ...args: unknown[]
    ) => void;
    dataLayer?: unknown[];
  }
}

// All analytics event names. Events added for the launch funnel (Task #106)
// are marked with [funnel]. Events added for conversion sections (Task #113)
// are marked with [conversion]. Pre-existing events are preserved unchanged.
// `app_store_click` and `google_play_click` are reserved for the post-launch
// store-badge wiring — the badges were intentionally removed from the
// marketing surface until real listings exist.
export type TrackedEvent =
  // Pre-existing events
  | "hero_create_mission_click"
  | "secondary_how_it_works_click"
  | "pricing_card_click"
  | "faq_open"
  | "contact_submit"
  | "start_mission_click"
  | "app_store_click"
  | "google_play_click"
  | "ai_summary_view"
  | "social_facebook_click"
  // Launch funnel events [funnel]
  | "launch_updates_view"
  | "launch_updates_submit"
  | "launch_updates_success"
  | "hero_launch_updates_click"
  | "hero_signup_click"
  | "pricing_card_signup_click"
  | "launch_updates_redirect_to_create"
  | "hero_how_it_works_click"
  | "pricing_cta_click"
  | "facebook_click"
  | "contact_click"
  | "create_mission_click"
  | "join_invite_click"
  // Conversion section events [conversion]
  | "mission_preview_start"
  | "mission_preview_role_selected"
  | "mission_preview_style_selected"
  | "mission_preview_energy_selected"
  | "mission_preview_completed"
  | "mission_preview_cta_click"
  | "audience_card_click"
  | "christmas_eve_section_cta_click"
  | "sample_clue_cta_click"
  | "comparison_cta_click"
  | "share_click"
  // Task #155 — Santa AI Concierge & Family Magic Profile v1.
  // Allowed metadata: selectedStyle, selectedTone, selectedDifficulty,
  // hasAccessibilityConstraints, favoriteCharacterCount. NEVER include
  // free-text traditions, avoid topics, AI-generated content, or raw
  // prompts in any of these payloads.
  | "family_magic_profile_created"
  | "family_magic_profile_updated"
  | "family_magic_profile_archived"
  // Task #199 — Host-only AI drafting helper. Allowed metadata:
  // turnCount, usedToday, dailyLimit. NEVER include the Q&A text or
  // any field of the proposed draft.
  | "family_magic_profile_drafted"
  | "santa_suggestion_viewed"
  // Task #236 — Marketing SEO & conversion hardening. Public page views and
  // conversion funnel events. `waitlist_submitted` fires alongside the
  // pre-existing `launch_updates_success` for downstream funnel attribution.
  // `contact_clicked` is the canonical name for the contact intent event
  // (the pre-existing `contact_click` is preserved for back-compat).
  | "marketing_home_viewed"
  | "pricing_viewed"
  | "faq_viewed"
  | "early_access_clicked"
  | "waitlist_submitted"
  | "contact_clicked"
  // Task #245 — Support Inbox + Help Center v1
  | "support_link_clicked"
  // /create funnel events. Fire from the create wizard so we can see
  // where cold ad traffic drops inside the 6-step flow before paid
  // tests begin. UTM attribution + is_test_account flags are inherited
  // automatically through the shared track() helper.
  | "create_flow_started"
  | "create_step_viewed"
  | "create_step_completed"
  | "mission_preview_viewed"
  // Fired once per session when the user first sees a real magical
  // product moment (Santa intro, sample clue, Spirit Map effect, etc.)
  // — currently the /create magic_preview step.
  | "first_magical_value_viewed";

export function track(
  event: TrackedEvent,
  params?: Record<string, string | number | boolean>,
): void {
  if (typeof window === "undefined") return;
  const fn = window.gtag;
  if (typeof fn !== "function") return;
  // Attribution and test-mode flags merge FIRST so caller-provided
  // properties win on key collision (e.g. an explicit `source: "hero"`
  // is preserved alongside `utm_source: "facebook"`).
  const enriched = {
    ...getAttributionPayload(),
    ...getTestModeFlags(),
    ...(params ?? {}),
  };
  fn("event", event, enriched);
}

// Fire a GA4 page_view for the current document. The initial pageview is
// already emitted automatically by the gtag('config', GA_ID, ...) call in
// index.html, so this is for client-side SPA navigations only — call it
// from a router-effect AFTER the very first mount to avoid double-counting
// the landing page.
export function pageview(): void {
  if (typeof window === "undefined") return;
  const fn = window.gtag;
  if (typeof fn !== "function") return;
  fn("event", "page_view", {
    ...getAttributionPayload(),
    ...getTestModeFlags(),
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
    page_location: window.location.href,
  });
}
